/**
 * @file: buildAudioWaveform.ts
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 *
 * Compacts the raw mic-metering stream captured while recording a voice
 * message into a slim, fixed-size waveform persisted on the audio payload
 * (mirrors backend `ChatAudioPayload.waveform`). Kept small on purpose: a
 * few dozen 2-decimal samples is enough to draw a recognizable amplitude
 * strip in the bubble without bloating the message row.
 */

/** Target bar count — enough detail for a WeChat-style strip, still tiny. */
export const WAVEFORM_BUCKETS = 40;

/**
 * Downsample `samples` (normalized 0..1 mic levels) to at most
 * {@link WAVEFORM_BUCKETS} buckets, taking the peak per bucket, then
 * self-normalize so the loudest bucket fills the bar height regardless of
 * mic gain. Returns 2-decimal values to keep the JSON slim.
 */
export function buildAudioWaveform(
  samples: number[],
  buckets = WAVEFORM_BUCKETS,
): number[] {
  if (samples.length === 0) return [];
  const n = Math.min(buckets, samples.length);
  const peaks: number[] = [];
  for (let i = 0; i < n; i++) {
    const start = Math.floor((i * samples.length) / n);
    const end = Math.max(start + 1, Math.floor(((i + 1) * samples.length) / n));
    let peak = 0;
    for (let j = start; j < end; j++) {
      if (samples[j] > peak) peak = samples[j];
    }
    peaks.push(peak);
  }
  const max = Math.max(...peaks, 0.0001);
  return peaks.map(v => Math.round((v / max) * 100) / 100);
}
